import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute('/admin-console/organisation-management/organisations/$orgId/subscriptions')({
    staticData: {
        presentation: 'fullPage',
        metadata: {
            title: 'Subscriptions',
        },
        ui: {
            title: 'Subscriptions',
            subtitle: 'Manage organisation subscriptions here.',
        },
    },
    component: Subscriptions,
});

type Subscription = RouterOutputs['admin']['subscription']['listSubscriptions']['rows'][number];
type QueryParams = RouterInputs['admin']['subscription']['listSubscriptions'];
type OrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<Subscription, OrderBy>();

function Subscriptions() {
    const { orgId } = Route.useParams();

    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        where: {
            orgId,
        },
        orderBy: {
            id: 'desc',
        },
    });

    const { data: subscriptions, isLoading } = trpc.admin.subscription.listSubscriptions.useQuery(queryParams);

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
            align: 'center',
        }),
        columnHelper.display({
            id: 'service',
            header: 'Service',
            cell: ({ row }) => <Typography.Text>{row.Service?.name}</Typography.Text>,
        }),
        columnHelper.display({
            id: 'tier',
            header: 'Tier',
            cell: ({ row }) => <Typography.Text>{row.Tier?.code}</Typography.Text>,
        }),
        columnHelper.accessor('startedAt', {
            id: 'startedAt',
            header: 'Started At',
            sortable: 'startedAt',
            cell: ({ value }) => new Date(value).toLocaleDateString(),
        }),
        columnHelper.accessor('expiresAt', {
            id: 'expiresAt',
            header: 'Expires At',
            sortable: 'expiresAt',
            cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : 'Never'),
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: 'Status',
            sortable: 'isActive',
            cell: ({ value }) => (value ? 'Active' : 'Inactive'),
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: subscriptions?.rows || [],
        total: subscriptions?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: (params) => {
            setQueryParams((prev) => ({
                ...params,
                where: { ...prev.where },
            }));
        },
        pagination: {
            position: 'bottom',
        },
    });

    return (
        <PageController route={Route}>
            <PageLayout.Header
                features={[
                    {
                        feature: 'search',
                        onSearch(value) {
                            setQueryParams((prev) => ({
                                ...prev,
                                offset: 0,
                                where: value
                                    ? ({
                                          ...prev.where,
                                          OR: [
                                              { Service: { name: { contains: value, mode: 'insensitive' } } },
                                              { Tier: { code: { contains: value, mode: 'insensitive' } } },
                                          ],
                                      } satisfies QueryParams['where'])
                                    : ({
                                          orgId,
                                      } satisfies QueryParams['where']),
                            }));
                        },
                    },
                ]}
                primaryActionAddon={<Button.Add.Link variant="primary" label="New subscription" to="." />}
            />

            <PrismaTable {...tableProps} />
        </PageController>
    );
}
